import { SalaryManualTab } from "./SalaryManualTab";
import { LockGate } from "./RevenuePanel";
import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { ExtraFilters, matchFields } from "./EmpExtraFilters";
const SAL_FIELDS = [
  { k: "platform", label: "المنصة", get: (r) => r.platform },
  { k: "branch", label: "الفرع", get: (r) => r.branch },
  { k: "paymode", label: "طريقة الحساب", get: (r) => ({ target: "تارقت", distance: "مسافة", freelance: "فري لانسر" }[payModeOf(r)] || payModeOf(r)) },
  { k: "state", label: "حالة الراتب", get: (r) => (r.draft ? "بانتظار الاعتماد" : "معتمد") },
  { k: "linked", label: "الربط", get: (r) => (r.employee_id ? "مربوط بموظف" : "غير مربوط") },
];
import { PAY_TAGS, payModeOf, CompareChip, PayModeTag, CostSummary } from "./PayModeTags";
import { toast } from "sonner";
import { Wallet, Upload, Loader2, Trash2, ChevronDown, Pencil, MessageSquareWarning, Check, X, Clock, Plus, Download, LayoutTemplate, Banknote, Printer, Calculator, FileText, Search, Mail } from "lucide-react";
import { SalaryTargetDialog } from "./SalaryTargetDialog";
import { SalaryFinalReports } from "./SalaryFinalReports";
import { WaSendButton } from "./WaSendButton";
import { payslipPDFBlob } from "../lib/payslipPdf";
import { BulkPayslipEmailDialog } from "./BulkPayslipEmailDialog";
import { DebtHistoryDialog } from "./DebtHistoryDialog";
import { History, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { CompanyTabs } from "./SalaryCompanyTabs";
import { SalaryDraftsBar } from "./SalaryDraftsBar";
import { PayrollLedgerBar } from "./PayrollLedgerBar";
import { BulkMudadBar } from "./BulkMudadBar";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { SignaturePad } from "./SignaturePad";
import { SaudiRiyal } from "./SaudiRiyal";
import { printSalaryReceipt } from "../lib/custodyReceipt";
import { issueAndPrintFreelance } from "../lib/freelanceInvoice";
import { FreelanceInvoiceLogDialog } from "./FreelanceInvoiceLogDialog";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { normSearch } from "../lib/searchText";
import { useLang } from "../i18n/LanguageContext";

const latinNum = (x) => String(x).replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d)).replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d)).replace(/[٫,]/g, ".").replace(/[^\d.-]/g, "");

const money = (n) => (
  <span className="inline-flex items-center gap-1" dir="ltr">
    {Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}
    <SaudiRiyal />
  </span>
);

const OBJ_STATUS = {
  pending: { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  reviewing: { cls: "bg-sky-50 text-sky-700 border-sky-200", Icon: Clock },
  resolved: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Check },
  rejected: { cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: X },
};

const EditSalaryDialog = ({ row, fields, onClose, onSaved }) => {
  const { t, dir } = useLang();
  const inputFields = fields.filter((f) => !f.derived);
  const [vals, setVals] = useState(() => {
    const v = {};
    inputFields.forEach((f) => { v[f.key] = (row.values || {})[f.key] ?? 0; });
    return v;
  });
  const [name, setName] = useState(row.employee_name || "");
  const [busy, setBusy] = useState(false);
  const num = (x) => Number(x || 0);
  const total = inputFields.filter((f) => f.group !== "deduct").reduce((s, f) => s + num(vals[f.key]), 0);
  const net = total - inputFields.filter((f) => f.group === "deduct").reduce((s, f) => s + num(vals[f.key]), 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.updateSalary(row.id, { employee_name: name, values: vals });
      toast.success(t("dash.sal.updated"));
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || t("dash.sal.saveFail")); }
    finally { setBusy(false); }
  };

  const group = (g) => inputFields.filter((f) => f.group === g);
  const Section = ({ title, items }) => (
    items.length === 0 ? null : (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-slate-500">{title}</p>
      <div className="grid grid-cols-2 gap-2">
        {items.map((f) => (
          <label key={f.key} className="block">
            <span className="text-[11px] text-slate-500">{f.label}</span>
            <Input type="text" inputMode="decimal" data-testid={`salary-edit-${f.key}`} value={vals[f.key] ?? ""}
              onChange={(e) => setVals((v) => ({ ...v, [f.key]: latinNum(e.target.value) }))}
              className="h-8 text-sm font-mono-nums" dir="ltr" />
          </label>
        ))}
      </div>
    </div>
    )
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir={dir} data-testid="salary-edit-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading text-lg">{t("dash.sal.editTitle")} {row.employee_name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <label className="block">
            <span className="text-[11px] text-slate-500">{t("dash.sal.name")}</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-sm" data-testid="salary-edit-name" />
          </label>
          {Section({ title: t("dash.sal.secBase"), items: group("base") })}
          {Section({ title: t("dash.sal.secAllow"), items: group("allow") })}
          {Section({ title: t("dash.sal.secDeduct"), items: group("deduct") })}
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
            <div className="rounded-xl bg-slate-50 p-3"><p className="text-[11px] text-slate-500">{t("dash.sal.gross")}</p><p className="font-heading font-bold text-slate-800 font-mono-nums">{money(total)}</p></div>
            <div className="rounded-xl bg-emerald-50 p-3"><p className="text-[11px] text-emerald-700">{t("dash.sal.net")}</p><p className="font-heading font-bold text-emerald-700 font-mono-nums">{money(net)}</p></div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t("dash.common.cancel")}</Button>
          <Button onClick={save} disabled={busy} data-testid="salary-edit-save" className="bg-blue-600 hover:bg-blue-700">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("dash.common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RespondDialog = ({ obj, onClose, onSaved }) => {
  const { t, dir } = useLang();
  const [status, setStatus] = useState(obj.status === "pending" ? "reviewing" : obj.status);
  const [response, setResponse] = useState(obj.manager_response || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.respondSalaryObjection(obj.id, { status, response }); toast.success(t("dash.sal.respSent")); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || t("dash.sal.saveFail")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md font-body" dir={dir} data-testid="objection-respond-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading text-lg">{t("dash.sal.respTitle")} {obj.employee_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <p className="text-[11px] text-slate-400 mb-1">{t("dash.sal.reasonMonth")} {obj.month}</p>{obj.reason}
          </div>
          <label className="block">
            <span className="text-[11px] text-slate-500">{t("dash.sal.statusLabel")}</span>
            <select data-testid="objection-status-select" value={status} onChange={(e) => setStatus(e.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="reviewing">{t("dash.sal.objSt.reviewing")}</option>
              <option value="resolved">{t("dash.sal.objSt.resolved")}</option>
              <option value="rejected">{t("dash.sal.objSt.rejected")}</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] text-slate-500">{t("dash.sal.respNote")}</span>
            <Textarea data-testid="objection-response-text" value={response} onChange={(e) => setResponse(e.target.value)} rows={3} className="mt-1 text-sm" placeholder={t("dash.sal.respPh")} />
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t("dash.common.cancel")}</Button>
          <Button onClick={save} disabled={busy} data-testid="objection-respond-save" className="bg-blue-600 hover:bg-blue-700">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("dash.sal.sendResp")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ManualSalaryDialog = ({ month, onClose, onSaved }) => {
  const { t, dir } = useLang();
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("standard");
  const [name, setName] = useState("");
  const [rid, setRid] = useState("");
  const [vals, setVals] = useState({});
  const [m, setM] = useState(month);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.salaryTemplates().then((d) => setTemplates(d.templates || [])).catch(() => {}); }, []);
  const tpl = templates.find((t2) => t2.id === tplId) || templates[0];
  const fields = tpl?.fields || [];
  const num = (x) => Number(x || 0);
  const total = fields.filter((f) => f.type !== "deduction").reduce((s, f) => s + num(vals[f.key]), 0);
  const net = total - fields.filter((f) => f.type === "deduction").reduce((s, f) => s + num(vals[f.key]), 0);
  const save = async () => {
    if (!name.trim()) { toast.error(t("dash.sal.warnName")); return; }
    setBusy(true);
    try {
      await api.manualSalary({ month: m, template_id: tplId, employee_name: name.trim(), rider_id: rid.trim(), values: vals });
      toast.success(t("dash.sal.saved"));
      onSaved(m);
    } catch (e) { toast.error(e?.response?.data?.detail || t("dash.sal.saveFail")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir={dir} data-testid="salary-manual-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading text-lg">{t("dash.sal.manualTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-[11px] text-slate-500">{t("dash.sal.month")}</span>
              <Input type="month" value={m} onChange={(e) => setM(e.target.value)} className="h-8 text-sm" data-testid="salary-manual-month" /></label>
            <label className="block"><span className="text-[11px] text-slate-500">{t("dash.sal.template")}</span>
              <select data-testid="salary-manual-template" value={tplId} onChange={(e) => { setTplId(e.target.value); setVals({}); }}
                className="mt-1 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                {templates.map((t2) => <option key={t2.id} value={t2.id}>{t2.name}</option>)}
              </select></label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-[11px] text-slate-500">{t("dash.sal.empName")}</span>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-sm" data-testid="salary-manual-name" /></label>
            <label className="block"><span className="text-[11px] text-slate-500">{t("dash.sal.riderOpt")}</span>
              <Input value={rid} onChange={(e) => setRid(e.target.value)} className="h-8 text-sm" dir="ltr" /></label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {fields.map((f) => (
              <label key={f.key} className="block">
                <span className={`text-[11px] ${f.type === "deduction" ? "text-rose-500" : "text-slate-500"}`}>{f.label}{f.type === "deduction" ? t("dash.sal.deductSuffix") : ""}</span>
                <Input type="text" inputMode="decimal" dir="ltr" data-testid={`salary-manual-${f.key}`} value={vals[f.key] ?? ""}
                  onChange={(e) => setVals((v) => ({ ...v, [f.key]: latinNum(e.target.value) }))} className="h-8 text-sm font-mono-nums" />
              </label>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
            <div className="rounded-xl bg-slate-50 p-3"><p className="text-[11px] text-slate-500">{t("dash.sal.gross")}</p><p className="font-heading font-bold text-slate-800 font-mono-nums">{money(total)}</p></div>
            <div className="rounded-xl bg-emerald-50 p-3"><p className="text-[11px] text-emerald-700">{t("dash.sal.net")}</p><p className="font-heading font-bold text-emerald-700 font-mono-nums">{money(net)}</p></div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t("dash.common.cancel")}</Button>
          <Button onClick={save} disabled={busy} data-testid="salary-manual-save" className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("dash.common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TemplatesDialog = ({ onClose }) => {
  const { t, dir } = useLang();
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("");
  const [rows, setRows] = useState([{ label: "", type: "allowance" }]);
  const [busy, setBusy] = useState(false);
  const load = () => api.salaryTemplates().then((d) => setTemplates(d.templates || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    const fields = rows.filter((r) => r.label.trim());
    if (!name.trim() || fields.length === 0) { toast.error(t("dash.sal.warnTpl")); return; }
    setBusy(true);
    try { await api.createSalaryTemplate(name.trim(), fields); toast.success(t("dash.sal.tplCreated")); setName(""); setRows([{ label: "", type: "allowance" }]); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("dash.sal.createFail")); }
    finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm(t("dash.sal.confirmDelTpl"))) return; try { await api.deleteSalaryTemplate(id); load(); } catch (e) { toast.error(e?.response?.data?.detail || t("dash.sal.delFail")); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir={dir} data-testid="salary-templates-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading text-lg">{t("dash.sal.tplTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {templates.map((tpl) => (
            <div key={tpl.id} data-testid={`salary-template-${tpl.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2">
              <div><p className="text-sm font-semibold text-slate-800">{tpl.name}{tpl.builtin ? <span className="mr-1 text-[10px] rounded bg-slate-100 text-slate-500 px-1">{t("dash.sal.builtin")}</span> : null}</p>
                <p className="text-[11px] text-slate-400">{(tpl.fields || []).length} {t("dash.sal.itemsSuffix")}</p></div>
              {!tpl.builtin && <button data-testid={`salary-template-del-${tpl.id}`} onClick={() => del(tpl.id)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
        <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50/40 p-3 space-y-2">
          <p className="text-sm font-semibold text-slate-700">{t("dash.sal.createTpl")}</p>
          <Input placeholder={t("dash.sal.tplNamePh")} value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-sm" data-testid="template-name" />
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input placeholder={t("dash.sal.itemNamePh")} value={r.label} data-testid={`template-field-label-${i}`}
                onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="h-8 text-sm flex-1" />
              <select value={r.type} data-testid={`template-field-type-${i}`}
                onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, type: e.target.value } : x))}
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm">
                <option value="allowance">{t("dash.sal.typeAllow")}</option>
                <option value="deduction">{t("dash.sal.typeDeduct")}</option>
              </select>
              <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} className="p-1 text-slate-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <div className="flex items-center justify-between">
            <button data-testid="template-add-field" onClick={() => setRows((rs) => [...rs, { label: "", type: "allowance" }])} className="text-xs text-blue-600 flex items-center gap-1"><Plus className="h-3 w-3" /> {t("dash.sal.addItem")}</button>
            <Button onClick={create} disabled={busy} size="sm" data-testid="template-create-btn" className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("dash.sal.saveTpl")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TotalsDialog = ({ mode, data, onClose }) => {
  const { t, dir } = useLang();
  const key = mode === "net" ? "net_salary" : "total_salary";
  const rows = [...(data?.rows || [])];
  const total = mode === "net" ? data?.totals?.net : data?.totals?.gross;
  const title = mode === "net" ? t("dash.sal.totalNet") : t("dash.sal.totalGross", { m: data?.month || "—" });
  const globalFields = data?.fields || [];
  const fieldsList = globalFields.length ? globalFields : (rows[0]?.fields || []);
  const agg = {};
  fieldsList.forEach((f) => { agg[f.key] = rows.reduce((s, r) => s + Number((r.values || {})[f.key] || 0), 0); });
  const totalOrders = rows.reduce((s, r) => s + Number(r.completed_orders || 0), 0);
  const grpCls = (g) => (g === "deduct" ? "text-rose-600" : g === "net" ? "font-bold text-emerald-700" : g === "total" ? "font-bold text-slate-800" : "text-slate-600");
  const special = fieldsList.filter((f) => f.group === "total" || f.group === "net");
  const normal = fieldsList.filter((f) => f.group !== "total" && f.group !== "net");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl font-body max-h-[90vh] overflow-y-auto" dir={dir} data-testid="salary-totals-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-heading text-lg">{mode === "net" ? "ملخص صافي المستحق للشركة" : "ملخص إجمالي رواتب الشركة"}</DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription></DialogHeader>

        <div className="grid grid-cols-3 gap-2 mb-1">
          <div className={`rounded-xl border p-3 ${mode === "net" ? "border-emerald-100 bg-emerald-50" : "border-blue-100 bg-blue-50"}`}>
            <p className={`text-[11px] ${mode === "net" ? "text-emerald-700" : "text-blue-700"}`}>{title}</p>
            <p className={`font-heading text-lg font-bold font-mono-nums mt-0.5 ${mode === "net" ? "text-emerald-700" : "text-blue-700"}`}>{money(total)}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-[11px] text-slate-500">عدد الموظفين</p>
            <p className="font-heading text-lg font-bold text-slate-800 font-mono-nums mt-0.5">{rows.length}</p>
          </div>
          <div className="rounded-xl border border-amber-100 bg-amber-50 p-3" data-testid="salary-company-orders">
            <p className="text-[11px] text-amber-700">إجمالي الطلبات</p>
            <p className="font-heading text-lg font-bold text-amber-700 font-mono-nums mt-0.5" dir="ltr">{totalOrders.toLocaleString("en-US")}</p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="salary-company-summary">
          <p className="text-sm font-bold text-slate-700 mb-1">إجمالي بنود الشركة لشهر {data?.month || "—"}</p>
          <p className="text-[11px] text-slate-400 mb-3">مجموع كل بند لكل الموظفين (وليس رواتب كل موظف على حدة).</p>
          {special.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              {special.map((f) => (
                <div key={f.key} data-testid={`salary-agg-${f.key}`}
                  className={`rounded-xl border p-3 ${f.group === "net" ? "border-emerald-100 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                  <p className={`text-[11px] ${f.group === "net" ? "text-emerald-700" : "text-slate-500"}`}>{f.label}</p>
                  <p className={`font-heading text-xl font-bold font-mono-nums mt-0.5 ${f.group === "net" ? "text-emerald-700" : "text-slate-800"}`}>{money(agg[f.key])}</p>
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
            {normal.map((f) => (
              <div key={f.key} data-testid={`salary-agg-${f.key}`} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5">
                <span className={grpCls(f.group)}>{f.label}</span>
                <span className={`font-mono-nums ${grpCls(f.group)}`}>{money(agg[f.key])}</span>
              </div>
            ))}
            {fieldsList.length === 0 && <p className="text-center text-slate-400 py-4 text-sm sm:col-span-2 lg:col-span-3">{t("dash.sal.noData")}</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CashReceiptsList = ({ data, onReprint, onDelete, canEdit }) => {
  const [fMonth, setFMonth] = useState("all");
  const all = data?.receipts || [];
  const months = Array.from(new Set(all.map((r) => r.month).filter(Boolean))).sort().reverse();
  const receipts = fMonth === "all" ? all : all.filter((r) => r.month === fMonth);
  if (!data) return <div className="py-12 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  return (
    <div className="space-y-3">
      {all.length > 0 && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <select data-testid="cash-receipts-month-filter" value={fMonth} onChange={(e) => setFMonth(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
            <option value="all">كل الأشهر</option>
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <a href={api.cashReceiptsExportUrl(fMonth === "all" ? "" : fMonth)} data-testid="cash-receipts-export-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-3 h-9 text-sm font-medium text-emerald-700 hover:bg-emerald-50">
            <Download className="h-4 w-4" /> تصدير Excel
          </a>
        </div>
      )}
      {receipts.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-12 text-center text-slate-400" data-testid="cash-receipts-empty">
          {all.length === 0 ? 'لا توجد سندات قبض نقدي بعد. أنشئ سندًا من زر "سند قبض نقدي" في كشف الرواتب.' : "لا توجد سندات لهذا الشهر."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="cash-receipts-list">
          {receipts.map((r) => (
            <div key={r.id} data-testid={`cash-receipt-${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-3.5 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 flex items-center gap-2">
                  <Banknote className="h-4 w-4 text-emerald-600 shrink-0" />{r.employee_name}
                  {r.matched ? <span className="text-[10px] rounded bg-emerald-50 text-emerald-600 px-1.5 py-0.5">مربوط</span>
                    : <span className="text-[10px] rounded bg-amber-50 text-amber-600 px-1.5 py-0.5">غير مربوط</span>}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span dir="ltr">{r.receipt_no}</span>
                  {r.national_id && <span>هوية: <span dir="ltr">{r.national_id}</span></span>}
                  <span>شهر {r.month}</span>
                  <span className="text-slate-300">{(r.created_at || "").slice(0, 10)}</span>
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-heading font-bold text-emerald-700 font-mono-nums">{money(r.amount)}</span>
                <button data-testid={`cash-receipt-print-${r.id}`} onClick={() => onReprint(r)} title="إعادة طباعة"
                  className="p-2 rounded-lg hover:bg-blue-50 text-blue-600"><Printer className="h-4 w-4" /></button>
                {canEdit && (
                  <button data-testid={`cash-receipt-del-${r.id}`} onClick={() => onDelete(r)} title="حذف"
                    className="p-2 rounded-lg hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CashReceiptDialog = ({ month, onClose, onSaved }) => {
  const { dir } = useLang();
  const sigRef = useRef(null);
  const [m, setM] = useState(month);
  const [nid, setNid] = useState("");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [matched, setMatched] = useState(false);
  const [busy, setBusy] = useState(false);

  const onNidChange = async (v) => {
    setNid(v);
    const digits = v.replace(/\D/g, "");
    if (digits.length < 8) { setMatched(false); return; }
    try {
      const r = await api.salaryEmployeeLookup(digits);
      if (r.found) { setName(r.name || ""); setMatched(true); } else setMatched(false);
    } catch { /* ignore */ }
  };

  const save = async () => {
    if (!name.trim()) { toast.error("أدخل اسم الموظف"); return; }
    if (!(Number(amount) > 0)) { toast.error("أدخل مبلغًا صحيحًا"); return; }
    const signature_data = sigRef.current?.getSignature() || null;
    setBusy(true);
    try {
      const r = await api.cashSalaryReceipt({ month: m, employee_name: name.trim(), national_id: nid.trim(), amount: Number(amount), note: note.trim(), signature_data });
      toast.success("تم حفظ سند القبض وربطه برواتب الموظف");
      let company = {};
      try { company = await api.getCompany(); } catch { /* ignore */ }
      printSalaryReceipt({ ...r.receipt, signature_dataurl: signature_data }, company);
      onSaved(m);
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الحفظ"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir={dir} data-testid="salary-cash-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-lg flex items-center gap-2"><Banknote className="h-5 w-5 text-emerald-600" /> سند قبض راتب نقدي</DialogTitle>
          <DialogDescription>أدخل بيانات الموظف والمبلغ ووقّع، ويُربط تلقائيًا برواتب الموظف عند مطابقة رقم الهوية.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-[11px] text-slate-500">الشهر</span>
              <Input type="month" value={m} onChange={(e) => setM(e.target.value)} className="h-9 text-sm" data-testid="salary-cash-month" /></label>
            <label className="block"><span className="text-[11px] text-slate-500">المبلغ (نقدًا)</span>
              <Input type="number" dir="ltr" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-9 text-sm font-mono-nums" data-testid="salary-cash-amount" placeholder="0.00" /></label>
          </div>
          <label className="block"><span className="text-[11px] text-slate-500">رقم الهوية</span>
            <Input dir="ltr" value={nid} onChange={(e) => onNidChange(e.target.value)} className="h-9 text-sm font-mono-nums" data-testid="salary-cash-nid" placeholder="10xxxxxxxx" /></label>
          <label className="block">
            <span className="text-[11px] text-slate-500 flex items-center gap-1.5">اسم الموظف
              {matched && <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600"><Check className="h-3 w-3" /> مطابق لموظف مسجّل</span>}</span>
            <Input value={name} onChange={(e) => { setName(e.target.value); setMatched(false); }} className="h-9 text-sm" data-testid="salary-cash-name" /></label>
          <label className="block"><span className="text-[11px] text-slate-500">ملاحظات</span>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="text-sm" data-testid="salary-cash-note" placeholder="اختياري" /></label>
          <div>
            <span className="text-[11px] text-slate-500">توقيع المستلم</span>
            <div className="mt-1"><SignaturePad ref={sigRef} height={150} /></div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={save} disabled={busy} data-testid="salary-cash-save" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />} حفظ وطباعة السند
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SalariesPanel = (props) => {
  const [unlocked, setUnlocked] = useState(false);
  if (!unlocked) return <LockGate kind="salary" onUnlock={() => setUnlocked(true)} />;
  return <SalariesInner {...props} />;
};

const SalariesInner = ({ canEdit }) => {
  const { company } = useAuth();
  const [ctFilter, setCtFilter] = useState("");
  const [pmFilter, setPmFilter] = useState("");
  const [sxf, setSxf] = useState({});
  const [q, setQ] = useState("");
  const [comp, setComp] = useState("");
  const [stTabs, setStTabs] = useState([]);
  const { t, dir } = useLang();
  const [tab, setTab] = useState("sheet");
  const [selSal, setSelSal] = useState(() => new Set());
  const togSal = (id) => setSelSal((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const [bulkMailOpen, setBulkMailOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [objData, setObjData] = useState(null);
  const [cashData, setCashData] = useState(null);
  const [respObj, setRespObj] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [cashOpen, setCashOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [stOpen, setStOpen] = useState(false);
  useEffect(() => { api.salaryTargetSettings().then((d) => setStTabs((d.platform_tabs || []).map((x) => x.platform))).catch(() => {}); }, [stOpen]);
  const [debtOpen, setDebtOpen] = useState(false);
  const [flLogOpen, setFlLogOpen] = useState(false);
  const sheetRef = useRef(null);
  const [sheetBusy, setSheetBusy] = useState("");
  const sheetMonth = () => month || new Date().toISOString().slice(0, 7);
  const dlSheet = async () => {
    setSheetBusy("dl");
    try {
      const b = await api.fixedSheetDownload(sheetMonth());
      const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `بنود_ثابتة_${sheetMonth()}.xlsx`; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error((e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : e?.response?.data?.detail) || "تعذّر التصدير"); } finally { setSheetBusy(""); }
  };
  const upSheet = async (f) => {
    if (!f) return;
    setSheetBusy("up");
    try {
      const r = await api.fixedSheetUpload(sheetMonth(), f);
      toast.success(`تم رفع ${r.rows} سطر (${r.items} بند) وحساب رواتب ${r.count} مندوب كمسودة`);
      load(sheetMonth());
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر رفع الشيت"); } finally { setSheetBusy(""); if (sheetRef.current) sheetRef.current.value = ""; }
  };
  const printFl = (list) => issueAndPrintFreelance(list, fields, company, month, api.issueFreelanceInvoices)
    .then((ok) => { if (!ok) toast.error("فعّل النوافذ المنبثقة للطباعة"); })
    .catch((e) => toast.error(e?.response?.data?.detail || "تعذّر إصدار الفاتورة"));
  const [totalsView, setTotalsView] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async (m) => {
    try { const d = await api.salariesOverview(m); setData(d); if (!m && d.month) setMonth(d.month); }
    catch { toast.error(t("dash.sal.loadFail")); }
  }, [t]);
  const [swBusy, setSwBusy] = useState("");
  const switchMode = async (row, mode) => {
    setSwBusy(row.id);
    try {
      await api.salaryPayMode(row.employee_id, month || data?.month, mode);
      toast.success(mode ? `تم تحويل ${row.employee_name} إلى ${mode === "distance" ? "المسافة" : "التارقت"} — الراتب بانتظار الاعتماد` : "رجع للإعداد التلقائي");
      await load(month || data?.month);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التحويل"); }
    finally { setSwBusy(""); }
  };
  const loadObj = useCallback(async () => {
    try { setObjData(await api.salaryObjections()); } catch { /* ignore */ }
  }, []);
  const loadCash = useCallback(async () => {
    try { setCashData(await api.listCashReceipts()); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(month || undefined); setSelSal(new Set()); }, [month, load]);
  useEffect(() => { loadObj(); }, [loadObj]);
  useEffect(() => { if (tab === "cash") loadCash(); }, [tab, loadCash]);

  const reprintCash = async (r) => {
    let company = {};
    try { company = await api.getCompany(); } catch { /* ignore */ }
    printSalaryReceipt(r, company);
  };
  const delCash = async (r) => {
    if (!window.confirm(`حذف سند القبض ${r.receipt_no}؟ سيُحذف أيضًا صف الراتب المرتبط به.`)) return;
    try { await api.deleteCashReceipt(r.id); toast.success("تم حذف السند"); loadCash(); load(month); }
    catch (e) { toast.error(e?.response?.data?.detail || "فشل الحذف"); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const m = month || new Date().toISOString().slice(0, 7);
    setBusy(true);
    try {
      const r = await api.importSalaries(file, m);
      toast.success(t("dash.sal.imported", { n: r.matched + r.unmatched, m: r.matched }));
      if (r.unmatched) toast.info(t("dash.sal.unmatched", { n: r.unmatched }) + (r.unmatched_names?.length ? `: ${r.unmatched_names.slice(0, 4).join("، ")}` : ""));
      setMonth(m); load(m);
    } catch (err) { toast.error(err?.response?.data?.detail || t("dash.sal.uploadFail")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const delMonth = async () => {
    if (!month || !window.confirm(t("dash.sal.confirmDelMonth", { m: month }))) return;
    try { const r = await api.deleteSalaryMonth(month); toast.success(t("dash.sal.monthDeleted", { n: r.deleted })); load(month); }
    catch (err) { toast.error(err?.response?.data?.detail || t("dash.sal.delFail")); }
  };
  const delRow = async (id) => {
    if (!window.confirm(t("dash.sal.confirmDelRow"))) return;
    try { await api.deleteSalary(id); toast.success(t("dash.sal.rowDeleted")); load(month); }
    catch (err) { toast.error(err?.response?.data?.detail || t("dash.sal.delFail")); }
  };

  const fields = data?.fields || [];
  const allRows = data?.rows || [];
  const compOf = (r) => r.platform || "__emp";
  const comps = [...new Set([...stTabs, ...allRows.map(compOf), "__emp"])].sort((a, b) => (a === "__emp") - (b === "__emp")).map((k) => ({ key: k, count: allRows.filter((r) => compOf(r) === k).length }));
  const curComp = comps.some((c) => c.key === comp) ? comp : comps[0]?.key || "";
  const compRows = allRows.filter((r) => compOf(r) === curComp);
  const compTotals = { gross: compRows.reduce((a, r) => a + (Number((r.values || {}).total_salary) || 0), 0), net: compRows.reduce((a, r) => a + (Number((r.values || {}).net_salary) || 0), 0), count: compRows.length };
  const ctypes = [...new Set(compRows.map((r) => r.contract_type).filter(Boolean))];
  const nq = normSearch(q);
  const rows = (ctFilter ? compRows.filter((r) => (ctFilter === "__none" ? !r.contract_type : r.contract_type === ctFilter)) : compRows)
    .filter((r) => !pmFilter || (pmFilter === "better" ? (r.compare && ((r.compare.current === "target" && r.compare.distance_net > r.compare.target_net) || (r.compare.current === "distance" && r.compare.target_net > r.compare.distance_net))) : payModeOf(r) === pmFilter))
    .filter((r) => !nq || normSearch([r.employee_name, r.rider_id, r.national_id, r.iqama_number, r.employee_number].filter(Boolean).join(" ")).includes(nq))
    .filter((r) => matchFields(r, SAL_FIELDS, sxf));
  const counts = objData?.counts || {};
  const objections = objData?.objections || [];

  return (
    <div className="space-y-4" data-testid="salaries-panel" dir={dir}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2"><Wallet className="h-5 w-5 text-blue-600" /> {t("dash.sal.title")}</h3>
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          <button data-testid="salaries-tab-sheet" onClick={() => setTab("sheet")}
            className={`px-3 py-1.5 text-sm rounded-md transition ${tab === "sheet" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>{t("dash.sal.tabSheet")}</button>
          <button data-testid="salaries-tab-objections" onClick={() => setTab("objections")}
            className={`px-3 py-1.5 text-sm rounded-md transition relative ${tab === "objections" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>
            {t("dash.sal.tabObjections")}{counts.pending ? <span className="mr-1 inline-flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] h-4 min-w-4 px-1">{counts.pending}</span> : null}
          </button>
          <button data-testid="salaries-tab-cash" onClick={() => setTab("cash")}
            className={`px-3 py-1.5 text-sm rounded-md transition ${tab === "cash" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>
            سندات القبض
          </button>
          <button data-testid="salaries-tab-manual" onClick={() => setTab("manual")}
            className={`px-3 py-1.5 text-sm rounded-md transition ${tab === "manual" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>
            كشف يدوي
          </button>
          <button data-testid="salaries-tab-final-reports" onClick={() => setTab("final")}
            className={`px-3 py-1.5 text-sm rounded-md transition ${tab === "final" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>
            التقارير النهائية للرواتب
          </button>
        </div>
      </div>

      {tab === "final" ? <SalaryFinalReports month={month} setMonth={setMonth} canEdit={canEdit} /> : tab === "manual" ? <SalaryManualTab /> : tab === "sheet" ? (
        <>
          <CompanyTabs comps={comps} comp={curComp} setComp={setComp} />

          <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-2.5" data-testid="salaries-toolbar">
            <select data-testid="salaries-month-select" value={month} onChange={(e) => setMonth(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-semibold">
              {(data?.months || []).length === 0 && <option value="">—</option>}
              {(data?.months || []).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select data-testid="salaries-contract-filter" value={ctFilter} onChange={(e) => setCtFilter(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
              <option value="">كل أنواع العقود</option>
              {ctypes.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="__none">بدون نوع عقد</option>
            </select>
            <select data-testid="salaries-paymode-filter" value={pmFilter} onChange={(e) => setPmFilter(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
              <option value="">كل طرق الحساب</option>
              {Object.entries(PAY_TAGS).map(([k, [l]]) => <option key={k} value={k}>{l} ({compRows.filter((r) => payModeOf(r) === k).length})</option>)}
              <option value="better">الطريقة الأخرى أفضل له</option>
            </select>
            <ExtraFilters rows={compRows} fields={SAL_FIELDS} xf={sxf} setXf={setSxf} presetKey="sal" snapshot={{ sxf, q, ctFilter, pmFilter }}
              onApply={(st) => { setSxf(st.sxf || {}); setQ(st.q || ""); setCtFilter(st.ctFilter || ""); setPmFilter(st.pmFilter || ""); }} />
            {canEdit && (
              <div className="ms-auto flex flex-wrap items-center gap-2">
                <Button data-testid="salaries-target-btn" onClick={() => setStOpen(true)} className="gap-1.5 h-9 bg-indigo-600 hover:bg-indigo-700">
                  <Calculator className="h-4 w-4" /> إعدادات رواتب {curComp === "__emp" ? "الموظفين" : curComp || "الشركة"}
                </Button>
                {compRows.length > 0 && (
                  <a href={api.salaryExportUrl(month, curComp)} data-testid="salaries-export-btn"
                    className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 h-9 text-sm font-medium text-emerald-700 hover:bg-emerald-100">
                    <Download className="h-4 w-4" /> {t("dash.sal.exportBtn")}
                  </a>
                )}
                <DropdownMenu dir="rtl">
                  <DropdownMenuTrigger asChild><Button variant="outline" className="h-9 gap-1.5" data-testid="salaries-more-btn"><MoreHorizontal className="h-4 w-4" /> المزيد</Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-60 font-body">
                    {rows.length > 0 && <DropdownMenuItem data-testid="salaries-bulk-email-btn" onClick={() => setBulkMailOpen(true)}><Mail className="h-4 w-4 text-blue-600" /> إرسال كشوف الظاهرين بالإيميل ({rows.length})</DropdownMenuItem>}
                    <DropdownMenuItem data-testid="salaries-cash-receipt-btn" onClick={() => setCashOpen(true)}><Banknote className="h-4 w-4 text-emerald-600" /> سند قبض نقدي</DropdownMenuItem>
                    {rows.some((r) => r.is_freelance) && <DropdownMenuItem data-testid="salaries-freelance-all" onClick={() => printFl(rows.filter((r) => r.is_freelance))}><FileText className="h-4 w-4 text-indigo-600" /> فواتير الفري لانسر ({rows.filter((r) => r.is_freelance).length})</DropdownMenuItem>}
                    <DropdownMenuItem data-testid="salaries-fl-log-btn" onClick={() => setFlLogOpen(true)}><FileText className="h-4 w-4 text-indigo-600" /> سجل فواتير الفري لانسر</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="salaries-sheet-export" disabled={!!sheetBusy} onClick={dlSheet}><Download className="h-4 w-4 text-emerald-600" /> تصدير شيت البنود</DropdownMenuItem>
                    <DropdownMenuItem data-testid="salaries-sheet-upload" disabled={!!sheetBusy} onClick={() => sheetRef.current?.click()}><Upload className="h-4 w-4 text-emerald-600" /> رفع شيت البنود واحتساب</DropdownMenuItem>
                    <DropdownMenuItem data-testid="salaries-debt-btn" onClick={() => setDebtOpen(true)}><History className="h-4 w-4 text-rose-600" /> سجل الديون المرحّلة</DropdownMenuItem>
                    {data?.totals?.count > 0 && <><DropdownMenuSeparator /><DropdownMenuItem data-testid="salaries-delete-month-btn" onClick={delMonth} className="text-rose-600"><Trash2 className="h-4 w-4" /> {t("dash.sal.delMonth")}</DropdownMenuItem></>}
                  </DropdownMenuContent>
                </DropdownMenu>
                {sheetBusy && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
                <input ref={sheetRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => upSheet(e.target.files?.[0])} data-testid="salaries-sheet-file" />
              </div>
            )}
          </div>
          {canEdit && <SalaryDraftsBar month={month} onPublished={() => load(month)} />}
          {canEdit && <PayrollLedgerBar month={month} refresh={data} />}

          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <button type="button" data-testid="salary-total-gross-card" onClick={() => setTotalsView("gross")}
              className="text-start rounded-2xl border border-blue-100 bg-blue-50 p-3 sm:p-4 transition-colors hover:border-blue-300 cursor-pointer">
              <p className="text-[11px] sm:text-xs text-blue-700">{t("dash.sal.totalGross", { m: data?.month || "—" })}</p>
              <p className="font-heading text-lg sm:text-2xl font-bold text-blue-700 font-mono-nums mt-1">{money(compTotals.gross)}</p>
            </button>
            <button type="button" data-testid="salary-total-net-card" onClick={() => setTotalsView("net")}
              className="text-start rounded-2xl border border-emerald-100 bg-emerald-50 p-3 sm:p-4 transition-colors hover:border-emerald-300 cursor-pointer">
              <p className="text-[11px] sm:text-xs text-emerald-700">{t("dash.sal.totalNet")}</p>
              <p className="font-heading text-lg sm:text-2xl font-bold text-emerald-700 font-mono-nums mt-1">{money(compTotals.net)}</p>
            </button>
            <div data-testid="salary-emp-count-card" className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
              <p className="text-[11px] sm:text-xs text-slate-500">{t("dash.sal.empCount")}</p>
              <p className="font-heading text-lg sm:text-2xl font-bold text-slate-800 font-mono-nums mt-1">{compTotals.count}</p>
            </div>
          </div>
          <CostSummary rows={compRows} />

          <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-blue-100 bg-blue-50/40 p-2" data-testid="salaries-table-search">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input data-testid="salaries-table-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث في الرواتب: الاسم، رقم المندوب، الهوية، الرقم الوظيفي"
                className="h-10 bg-white pr-9 text-sm" />
            </div>
            {q && <button type="button" onClick={() => setQ("")} className="text-xs font-semibold text-rose-600 hover:underline" data-testid="salaries-table-search-clear">مسح</button>}
            <span className="text-xs text-slate-500" data-testid="salaries-table-search-count">{rows.length} من {compRows.length}</span>
          </div>

          {canEdit && rows.length > 0 && <BulkMudadBar selected={selSal} setSelected={setSelSal} visibleIds={rows.map((r) => r.id)} onDone={() => load(month)} />}

          <div className="sm:hidden space-y-2" data-testid="salaries-cards">
            {!data ? (
              <div className="py-10 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
            ) : rows.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">{t("dash.sal.noData")}</div>
            ) : rows.map((row, i) => (
              <div key={row.id} data-testid={`salary-card-${row.id}`} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <button onClick={() => setOpen(open === i ? null : i)} className="w-full text-right p-3 flex items-center justify-between gap-2">
                  {canEdit && <input type="checkbox" checked={selSal.has(row.id)} onClick={(e) => e.stopPropagation()} onChange={() => togSal(row.id)} data-testid={`salary-check-m-${row.id}`} className="h-4 w-4 shrink-0 accent-rose-600" />}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 truncate">{row.employee_name}{!row.matched && <span className="mr-1 text-[10px] rounded bg-amber-50 text-amber-600 px-1 py-0.5">{t("dash.sal.noMatch")}</span>}<PayModeTag row={row} /></p>
                    <CompareChip row={row} onSwitch={canEdit ? switchMode : null} busy={swBusy === row.id} />
                    {row.rider_id && <p className="text-[11px] text-slate-400 font-mono-nums" dir="ltr">{row.rider_id}</p>}
                    {row.contract_type && <span className="text-[10px] rounded-full bg-indigo-50 px-1.5 py-0.5 text-indigo-700">{row.contract_type}</span>}
                  </div>
                  <div className="text-center shrink-0">
                    <p className="text-[10px] text-slate-400">{t("dash.sal.colNet")}</p>
                    <p className="font-mono-nums font-bold text-emerald-700">{money((row.values || {}).net_salary)}</p>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition shrink-0 ${open === i ? "rotate-180" : ""}`} />
                </button>
                {open === i && (
                  <div className="border-t border-slate-100 px-3 py-3">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                      {(row.evaluation || row.completed_orders || row.distance_km != null) && (
                        <div className="col-span-2 flex gap-4 text-xs text-slate-500 pb-1">
                          {row.evaluation ? <span>{t("dash.sal.evalLabel")} <b className="text-slate-700">{row.evaluation}</b></span> : null}
                          {row.completed_orders ? <span>{t("dash.sal.completedLabel")} <b className="text-slate-700 font-mono-nums">{row.completed_orders}</b></span> : null}
{row.distance_km != null ? <span data-testid={`salary-km-m-${row.id}`}>المسافة المستحقة: <b className="text-sky-700 font-mono-nums">{Number(row.distance_km).toLocaleString("en-US")} كم</b></span> : null}
                        </div>
                      )}
                      {(row.fields || fields).map((f) => (
                        <div key={f.key} className="flex items-center justify-between text-xs border-b border-slate-100 py-1">
                          <span className={f.group === "deduct" ? "text-rose-500" : f.group === "net" ? "font-semibold text-emerald-700" : f.group === "total" ? "font-semibold text-slate-700" : "text-slate-500"}>{f.label}</span>
                          <span className="font-mono-nums text-slate-700">{money((row.values || {})[f.key])}</span>
                        </div>
                      ))}
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-2 pt-2 mt-1 border-t border-slate-100">
                        <button data-testid={`salary-edit-m-${row.id}`} onClick={() => setEditRow(row)} className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-blue-50 text-blue-600 py-1.5 text-xs font-medium"><Pencil className="h-4 w-4" /> تعديل</button>
                        <button data-testid={`salary-del-m-${row.id}`} onClick={() => delRow(row.id)} className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-rose-50 text-rose-600 py-1.5 text-xs font-medium"><Trash2 className="h-4 w-4" /> حذف</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="hidden sm:block overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm text-right min-w-[900px]">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  {canEdit && <th className="px-3 py-2.5 w-8"><input type="checkbox" data-testid="salary-check-all" className="h-4 w-4 accent-rose-600"
                    checked={rows.length > 0 && rows.every((r) => selSal.has(r.id))} onChange={(e) => setSelSal(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} /></th>}
                  <th className="px-3 py-2.5 font-semibold">{t("dash.sal.colEmployee")}</th>
                  <th className="px-3 py-2.5 font-semibold">{t("dash.sal.colRider")}</th>
                  <th className="px-3 py-2.5 font-semibold">نوع العقد</th>
                  <th className="px-3 py-2.5 font-semibold">{t("dash.sal.colGross")}</th>
                  <th className="px-3 py-2.5 font-semibold">{t("dash.sal.colNet")}</th>
                  {canEdit && <th className="px-3 py-2.5 font-semibold w-20"></th>}
                  <th className="px-3 py-2.5 font-semibold w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!data ? (
                  <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400">{t("dash.sal.noData")}</td></tr>
                ) : rows.map((row, i) => (
                  <Fragment key={row.id}>
                    <tr key={row.id} data-testid={`salary-row-${row.id}`} className="hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpen(open === i ? null : i)}>
                      {canEdit && <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selSal.has(row.id)} onChange={() => togSal(row.id)} data-testid={`salary-check-${row.id}`} className="h-4 w-4 accent-rose-600" /></td>}
                      <td className="px-3 py-2.5 font-semibold text-slate-800">
                        {row.employee_name}{!row.matched && <span className="mr-1 text-[10px] rounded bg-amber-50 text-amber-600 px-1 py-0.5">{t("dash.sal.noMatch")}</span>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-500 font-mono-nums" dir="ltr">{row.rider_id || "—"}</td>
                      <td className="px-3 py-2.5 text-xs" data-testid={`salary-contract-${row.id}`}>{row.contract_type ? <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-700">{row.contract_type}</span> : "—"}{row.platform && <span className="ms-1 rounded-full bg-sky-50 px-2 py-0.5 text-sky-700" data-testid={`salary-platform-${row.id}`}>{row.platform}</span>}<PayModeTag row={row} /><CompareChip row={row} onSwitch={canEdit ? switchMode : null} busy={swBusy === row.id} /></td>
                      <td className="px-3 py-2.5 font-mono-nums text-slate-600">{money((row.values || {}).total_salary)}</td>
                      <td className="px-3 py-2.5 font-mono-nums font-bold text-emerald-700">{money((row.values || {}).net_salary)}</td>
                      {canEdit && (
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            {row.is_freelance && <button data-testid={`salary-invoice-btn-${row.id}`} title="فاتورة مستحقات" onClick={() => printFl([row])} className="p-1.5 rounded-md hover:bg-indigo-50 text-indigo-600"><FileText className="h-4 w-4" /></button>}
                            <WaSendButton employeeId={row.employee_id} employeeName={row.employee_name} refType="payslip" refId={row.id} testid={`salary-wa-${row.id}`} emailTitle={`كشف راتب شهر ${row.month || month}`} className="!h-7 !w-7"
                              text={`السلام عليكم ${row.employee_name || ""}\nمرفق كشف راتبك لشهر ${row.month || month}.\nصافي الراتب: ${Number((row.values || {}).net_salary || 0).toLocaleString("en-US")} ريال.`}
                              pdf={async () => ({ blob: await payslipPDFBlob({ ...row, month: row.month || month }, row.fields || fields, company?.name), filename: `كشف-راتب-${row.month || month}-${row.employee_name || ""}.pdf` })} />
                            <button data-testid={`salary-edit-btn-${row.id}`} onClick={() => setEditRow(row)} className="p-1.5 rounded-md hover:bg-blue-50 text-blue-600"><Pencil className="h-4 w-4" /></button>
                            <button data-testid={`salary-delete-btn-${row.id}`} onClick={() => delRow(row.id)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      )}
                      <td className="px-3 py-2.5"><ChevronDown className={`h-4 w-4 text-slate-400 transition ${open === i ? "rotate-180" : ""}`} /></td>
                    </tr>
                    {open === i && (
                      <tr key={`d-${row.id}`} className="bg-slate-50/50">
                        <td colSpan={8} className="px-4 py-3">
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5">
                            {(row.evaluation || row.completed_orders || row.distance_km != null) && (
                              <div className="col-span-2 sm:col-span-3 flex gap-4 text-xs text-slate-500 pb-1">
                                {row.evaluation ? <span>{t("dash.sal.evalLabel")} <b className="text-slate-700">{row.evaluation}</b></span> : null}
                                {row.completed_orders ? <span>{t("dash.sal.completedLabel")} <b className="text-slate-700 font-mono-nums">{row.completed_orders}</b></span> : null}
{row.distance_km != null ? <span data-testid={`salary-km-d-${row.id}`}>المسافة المستحقة: <b className="text-sky-700 font-mono-nums">{Number(row.distance_km).toLocaleString("en-US")} كم</b></span> : null}
                              </div>
                            )}
                            {(row.fields || fields).map((f) => (
                              <div key={f.key} className="flex items-center justify-between text-xs border-b border-slate-100 py-1">
                                <span className={f.group === "deduct" ? "text-rose-500" : f.group === "net" ? "font-semibold text-emerald-700" : f.group === "total" ? "font-semibold text-slate-700" : "text-slate-500"}>{f.label}</span>
                                <span className="font-mono-nums text-slate-700">{money((row.values || {})[f.key])}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : tab === "objections" ? (
        <div className="space-y-3" data-testid="salaries-objections-list">
          {!objData ? (
            <div className="py-12 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
          ) : objections.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white py-12 text-center text-slate-400">{t("dash.sal.noObjections")}</div>
          ) : objections.map((o) => {
            const st = OBJ_STATUS[o.status] || OBJ_STATUS.pending;
            return (
              <div key={o.id} data-testid={`objection-${o.id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-semibold text-slate-800 flex items-center gap-2"><MessageSquareWarning className="h-4 w-4 text-amber-500" />{o.employee_name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{t("dash.sal.monthPrefix")} {o.month} · {o.rider_id || "—"}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-[11px] rounded-full border px-2 py-0.5 ${st.cls}`}><st.Icon className="h-3 w-3" />{t(`dash.sal.objSt.${o.status}`)}</span>
                </div>
                <p className="mt-2 text-sm text-slate-600 bg-slate-50 rounded-lg p-2.5">{o.reason}</p>
                {o.manager_response && <p className="mt-2 text-xs text-slate-500 border-r-2 border-blue-300 pr-2">{t("dash.sal.yourResponse")} {o.manager_response}</p>}
                {canEdit && (
                  <div className="mt-3 flex justify-end">
                    <Button data-testid={`objection-respond-btn-${o.id}`} size="sm" variant="outline" onClick={() => setRespObj(o)} className="gap-1.5 border-blue-200 text-blue-600 hover:bg-blue-50">
                      <MessageSquareWarning className="h-4 w-4" /> {t("dash.sal.respondUpdate")}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <CashReceiptsList data={cashData} onReprint={reprintCash} onDelete={delCash} canEdit={canEdit} />
      )}

      {editRow && <EditSalaryDialog row={editRow} fields={editRow.fields || fields} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); load(month); }} />}
      {manualOpen && <ManualSalaryDialog month={month || new Date().toISOString().slice(0, 7)} onClose={() => setManualOpen(false)} onSaved={(m) => { setManualOpen(false); setMonth(m); load(m); }} />}
      {cashOpen && <CashReceiptDialog month={month || new Date().toISOString().slice(0, 7)} onClose={() => setCashOpen(false)} onSaved={(m) => { setCashOpen(false); setMonth(m); load(m); }} />}
      {tplOpen && <TemplatesDialog onClose={() => setTplOpen(false)} />}
      <DebtHistoryDialog open={debtOpen} onOpenChange={setDebtOpen} />
      <BulkPayslipEmailDialog open={bulkMailOpen} onOpenChange={setBulkMailOpen} rows={rows} fields={fields} month={month} companyName={company?.name} />
      <FreelanceInvoiceLogDialog open={flLogOpen} onOpenChange={setFlLogOpen} month={month} fields={fields} company={company} />
      <SalaryTargetDialog open={stOpen} onOpenChange={setStOpen} month={month} initialTab={curComp} onSaved={(m) => { setMonth(m); load(m); }} />
      {respObj && <RespondDialog obj={respObj} onClose={() => setRespObj(null)} onSaved={() => { setRespObj(null); loadObj(); }} />}
      {totalsView && <TotalsDialog mode={totalsView} data={{ ...data, rows: compRows, totals: compTotals }} onClose={() => setTotalsView(null)} />}
    </div>
  );
};
